<template>
  <v-card class="my-4">
    <v-card-title>
      Catalog feed
      <v-spacer />
      <v-btn
        icon
        title="Reload preview"
        :loading="loading"
        @click="loadPreview"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-subtitle>
      The catalog feed Google and Meta build dynamic remarketing ads from. One row per collection — a
      category, optionally narrowed by an industry or topic — never one row per template. Rows are a
      projection of the catalog content pages, so nothing here is edited by hand. Both platforms are
      published from the same rows and differ only in the columns they require.
    </v-card-subtitle>

    <v-card-text>
      <v-row align="center">
        <v-col
          cols="12"
          sm="3"
        >
          <v-text-field
            v-model.number="minTemplates"
            type="number"
            dense
            hide-details
            label="Min templates"
            hint="Collections with fewer are not advertised"
            persistent-hint
            @change="loadPreview"
          />
        </v-col>
        <v-col
          cols="12"
          sm="auto"
        >
          <div class="text-h4">
            <!-- A dash, not 0, until we've actually heard back — "0 rows in feed" is a real and
                 alarming state, and it shouldn't be shown before it's known to be true. -->
            <span v-if="firstLoad">—</span>
            <span v-else>{{ total }}</span>
          </div>
          <div class="caption grey--text">
            rows in feed
          </div>
        </v-col>
        <v-spacer />
        <v-col
          cols="12"
          sm="auto"
        >
          <v-btn
            color="primary"
            :loading="generating"
            :disabled="loading || total === 0"
            @click="confirmGenerate = true"
          >
            <v-icon left>
              mdi-cloud-upload
            </v-icon>
            Generate &amp; upload
          </v-btn>
        </v-col>
      </v-row>

      <!-- Last upload, per platform. Reported separately because the two uploads succeed and fail
           independently — a single line would have to pick one of two truths, and the one it
           dropped would be the one that broke. -->
      <v-alert
        v-for="platform in platforms"
        :key="platform.key"
        dense
        text
        :type="statusFor(platform.key) ? 'info' : 'warning'"
        class="mt-4 mb-0"
      >
        <template v-if="statusFor(platform.key)">
          <strong>{{ platform.label }}</strong> — last uploaded
          {{ statusFor(platform.key).generatedAt | normalDateWithTime }},
          {{ statusFor(platform.key).itemCount }} rows.
          <a
            :href="statusFor(platform.key).feedUrl"
            target="_blank"
            rel="noopener"
          >{{ statusFor(platform.key).feedUrl }}</a>
        </template>
        <template v-else>
          <strong>{{ platform.label }}</strong> — never uploaded. {{ platform.label }} has nothing to
          poll until this runs at least once.
        </template>
      </v-alert>
    </v-card-text>

    <!-- Distribution: a feed skewed to one category is obvious here without reading every row -->
    <v-card-text
      v-if="firstLoad"
      class="pt-0"
    >
      <v-skeleton-loader type="chip@6" />
    </v-card-text>

    <v-card-text
      v-else-if="byCategory.length"
      class="pt-0"
    >
      <div class="subtitle-2 mb-2">
        By category
        <span
          v-if="selectedCategory"
          class="grey--text font-weight-regular"
        >
          — showing {{ filteredTotal }} of {{ total }}
        </span>
      </div>
      <v-chip
        v-for="row in byCategory"
        :key="row.category"
        small
        class="mr-2 mb-2"
        :color="selectedCategory === row.category ? 'primary' : undefined"
        :outlined="selectedCategory === row.category"
        :close="selectedCategory === row.category"
        :disabled="loading"
        @click="toggleCategory(row.category)"
        @click:close="clearCategory"
      >
        {{ row.category }}
        <span
          class="ml-2"
          :class="selectedCategory === row.category ? '' : 'grey--text'"
        >{{ row.count }}</span>
      </v-chip>
    </v-card-text>

    <!-- First load gets a skeleton because there's nothing to keep on screen; refilters keep the
         existing rows under the table's own progress bar, so changing a filter doesn't collapse
         the layout and bounce the page. -->
    <v-skeleton-loader
      v-if="firstLoad"
      type="table-thead, table-tbody"
      class="px-4 pb-4"
    />

    <v-data-table
      v-else
      :headers="headers"
      :items="items"
      :loading="loading"
      loading-text="Building feed preview…"
      no-data-text="No collections match this filter."
      :items-per-page="50"
      :footer-props="{ 'items-per-page-options': [50, 100, 250, -1] }"
      dense
    >
      <template #[`item.imageUrl`]="{ item }">
        <v-img
          :src="item.imageUrl"
          max-width="60"
          max-height="45"
          contain
          class="my-1"
        />
      </template>
      <template #[`item.link`]="{ item }">
        <a
          :href="item.link"
          target="_blank"
          rel="noopener"
        >{{ item.link }}</a>
      </template>
    </v-data-table>

    <v-dialog
      v-model="confirmGenerate"
      max-width="480"
    >
      <v-card>
        <v-card-title>Upload feeds?</v-card-title>
        <v-card-text>
          This overwrites the live feeds at the public URLs Google and Meta poll. {{ total }} rows will
          replace whatever is there now, on both, using the saved minimum — not the preview value above.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="confirmGenerate = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="generating"
            @click="generate"
          >
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'RetargetingCatalogFeed',

  data() {
    return {
      loading: false,
      // Distinguishes "never loaded" from "reloading": the first pass has nothing to show and gets
      // a skeleton, later passes keep their rows so a filter change doesn't blank the screen.
      loaded: false,
      generating: false,
      confirmGenerate: false,
      minTemplates: 12,
      total: 0,
      filteredTotal: 0,
      selectedCategory: null,
      byCategory: [],
      items: [],
      // Keyed by platform, matching the API's { google, meta } shape. Either can be null on its own.
      status: {},
      platforms: [
        { key: 'google', label: 'Google' },
        { key: 'meta', label: 'Meta' }
      ],
      headers: [
        { text: '', value: 'imageUrl', sortable: false, width: 70 },
        { text: 'Feed ID', value: 'itemId' },
        { text: 'Title', value: 'title' },
        { text: 'Category', value: 'categoryName' },
        { text: 'Templates', value: 'templateCount' },
        { text: 'Landing page', value: 'link' }
      ]
    };
  },

  computed: {
    firstLoad() {
      return this.loading && !this.loaded;
    }
  },

  created() {
    this.loadStatus();
    this.loadPreview();
  },

  methods: {
    statusFor(platform) {
      return this.status[platform] || null;
    },

    // Clicking the selected chip clears it, so the chip is its own toggle as well as carrying an
    // explicit close icon — either gesture gets you back to the full feed.
    toggleCategory(category) {
      this.selectedCategory = this.selectedCategory === category ? null : category;
      this.loadPreview();
    },

    clearCategory() {
      this.selectedCategory = null;
      this.loadPreview();
    },

    loadPreview() {
      this.loading = true;
      const params = { minTemplates: this.minTemplates };
      if (this.selectedCategory) params.category = this.selectedCategory;

      this.$http
        .get('retargeting/previewCatalogFeed', { params })
        .then((response) => {
          this.total = response.data.total;
          this.filteredTotal = response.data.filteredTotal;
          this.byCategory = response.data.byCategory || [];
          this.items = response.data.items || [];
          this.loaded = true;
        })
        .catch((error) => {
          // httpClient's interceptor already pulled the API's own message onto error.message —
          // showing a generic string here would throw away the only thing that says what broke.
          this.$root.$alert('Error', error.message || 'Could not build the feed preview.');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadStatus() {
      this.$http
        .get('retargeting/catalogFeedStatus')
        .then((response) => {
          this.status = response.data || {};
        })
        .catch(() => {
          // Status is informational — a failure here shouldn't block the preview.
        });
    },

    generate() {
      this.generating = true;
      this.$http
        .post('retargeting/generateCatalogFeed')
        .then((response) => {
          this.confirmGenerate = false;
          const run = response.data || {};

          // A skipped run built rows but published nothing. Showing it as the new status would
          // read as a successful upload, so say so plainly and leave the last real status intact.
          // The guard is per platform: they skip for the same reason today, but merging them would
          // hide a half-published run the moment they stop agreeing.
          const skipped = this.platforms
            .map((p) => run[p.key])
            .filter((result) => result && result.skipped);

          if (skipped.length) {
            this.$root.$alert('Not uploaded', skipped[0].message);
          }

          this.platforms.forEach((p) => {
            const result = run[p.key];
            if (result && !result.skipped) this.$set(this.status, p.key, result);
          });
        })
        .catch(() => {
          // One platform can fail while the other publishes, so re-read rather than assuming
          // nothing changed — the alert says something broke, the status says what survived.
          this.loadStatus();
          this.$root.$alert('Error', 'Feed upload failed. Check each platform below for what published.');
        })
        .finally(() => {
          this.generating = false;
        });
    }
  }
};
</script>
